import { find, isEqual, omit } from 'lodash/fp';
import { Person } from '../../../../../global/entities/Person';
import { CUSTOMER_FORM_ID } from '../../../../../global/constants';
import { isPresent } from '../../../../../utils/is-present';

type RelationPersonIdentifier = { person: string };

type ExcludePersonFromRelation<T extends RelationPersonIdentifier> = Omit<
  T,
  'person'
>;

type MergedRelationPerson<T extends RelationPersonIdentifier> =
  ExcludePersonFromRelation<T> & Person;

type MergedRelationPersonWithoutIds<T extends RelationPersonIdentifier> = Omit<
  MergedRelationPerson<T>,
  'id' | 'personId' | 'relationId' | 'signerId'
>;

export function mapRelationsToPeople<T extends { person: string }>(
  id: string,
  relations: T[],
  people: Person[],
): (MergedRelationPerson<T> | MergedRelationPersonWithoutIds<T>)[] {
  function findBySsn(relation: T) {
    return find(
      (person) => isEqual(person.socialSecurityNumber, relation.person),
      people,
    );
  }

  function mergePerson(relation: T): MergedRelationPerson<T> | undefined {
    const omittedRelation = omit('person', relation);
    const person = findBySsn(relation);

    if (!person) {
      return undefined;
    }

    return {
      ...omittedRelation,
      ...person,
    };
  }

  const result = relations.map(mergePerson).filter(isPresent);
  const resultWithoutIds = result.map(
    omit(['id', 'personId', 'relationId', 'signerId', 'organizationId']),
  ) as MergedRelationPersonWithoutIds<T>[];

  return id === CUSTOMER_FORM_ID ? resultWithoutIds : result;
}
